import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useWorkflowStore } from '../stores/workflowStore';
import { getNodeDisplayLabel } from '../utils/nodeDisplayLabel';
import { useSchema } from '../contexts/SchemaContext';
import { Column } from '../types';
import { getDisplayColumns } from '../utils/schemaDisplay';

export interface InputColumn {
  column: Column;
  source: {
    nodeId: string;
    nodeType: string;
    nodeName: string;
  };
}

/**
 * 获取指定节点的输入列
 * - 优先使用直接上游节点在 SchemaContext 中注册的输出列
 * - 如果直接上游没有 Schema，则向上游递归追溯，直到找到有 Schema 的节点（通常是数据源）
 *
 * 这样可以保证：
 * - 删除列节点挂在「分组」「映射转换」等中间节点后面时，仍然能看到上游的数据列
 * - 当中间节点自己注册了输出 Schema 时，会优先使用中间节点的列信息
 *
 * @param nodeId 当前节点 ID
 * @param inputIndex 输入索引（0 表示第一个输入，1 表示第二个输入，以此类推），不传则返回所有输入
 * @returns 输入列数组或 undefined
 */
export function useNodeInputColumns(nodeId: string, inputIndex?: number): InputColumn[] | undefined {
  const { nodes, edges } = useWorkflowStore();
  const { availableSchemas, getNodeSchema } = useSchema();
  const { t, i18n } = useTranslation();

  return useMemo(() => {
    // 获取指向当前节点的边（按输入顺序排序）
    const incomingEdges = edges
      .filter(edge => edge.target === nodeId)
      .sort((a, b) => a.source.localeCompare(b.source));

    if (incomingEdges.length === 0) {
      return undefined;
    }

    // 递归获取某个节点的输出列（向上游追溯）；返回「显示列」：动态字段只显示公共名称
    const getNodeOutputColumns = (targetNodeId: string): Column[] | undefined => {
      // 1. 优先使用 SchemaContext 中已注册的 schema
      const schema = getNodeSchema(targetNodeId);
      if (schema) {
        const displayCols = getDisplayColumns(schema);
        if (displayCols.length > 0) return displayCols;
      }

      // 2. 如果没有 schema，尝试查找该节点，并向上游追溯
      const targetNode = nodes.find(n => n.id === targetNodeId);
      if (!targetNode) return undefined;

      // 对于数据源节点，如果没有 schema，就无法推断列
      if (['load_excel', 'load_csv', 'load_table', 'load_api', 'online_sheet'].includes(targetNode.type)) {
        return undefined;
      }

      // 查找指向该节点的上游边，取第一个输入继续向上游追溯
      const upstreamEdges = edges.filter(e => e.target === targetNodeId);
      if (upstreamEdges.length === 0) return undefined;

      const firstInput = upstreamEdges[0];
      return getNodeOutputColumns(firstInput.source);
    };

    // 如果指定了 inputIndex，只返回该索引对应的输入
    if (inputIndex !== undefined) {
      const edge = incomingEdges[inputIndex];
      if (!edge) {
        return undefined;
      }

      const sourceNode = nodes.find(n => n.id === edge.source);
      if (!sourceNode) {
        return undefined;
      }

      const columns = getNodeOutputColumns(edge.source);
      if (!columns || columns.length === 0) {
        return undefined;
      }

      return columns.map(column => ({
        column,
        source: {
          nodeId: sourceNode.id,
          nodeType: sourceNode.type,
          nodeName: getNodeDisplayLabel(sourceNode.type, sourceNode.data?.label, t, i18n),
        },
      }));
    }

    // 返回所有输入的列（合并）
    const allColumns: InputColumn[] = [];
    incomingEdges.forEach(edge => {
      const sourceNode = nodes.find(n => n.id === edge.source);
      if (!sourceNode) {
        return;
      }

      const columns = getNodeOutputColumns(edge.source);
      if (!columns || columns.length === 0) {
        return;
      }

      columns.forEach(column => {
        allColumns.push({
          column,
          source: {
            nodeId: sourceNode.id,
            nodeType: sourceNode.type,
            nodeName: getNodeDisplayLabel(sourceNode.type, sourceNode.data?.label, t, i18n),
          },
        });
      });
    });

    return allColumns.length > 0 ? allColumns : undefined;
  }, [nodeId, inputIndex, nodes, edges, availableSchemas, getNodeSchema, t, i18n]);
}

/**
 * 获取指定节点的所有输入 schemas
 * @param nodeId 当前节点 ID
 * @returns 输入 schema 数组
 */
export function useNodeInputSchemas(nodeId: string) {
  const { edges } = useWorkflowStore();
  const { getNodeSchema } = useSchema();

  return useMemo(() => {
    const incomingEdges = edges
      .filter(edge => edge.target === nodeId)
      .sort((a, b) => a.source.localeCompare(b.source));

    return incomingEdges.map(edge => {
      const schema = getNodeSchema(edge.source);
      return {
        edge,
        schema,
      };
    }).filter(item => item.schema !== undefined);
  }, [nodeId, edges, getNodeSchema]);
}
